import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import './PartnerMeeting.css';

const FINANCE_NAME = 'M/S SHREE MAHAKAL FINANCE AND LEASING (R)';

const DEFAULT_PARTNERS = [
    'Rajkumar Bande',
    'Siddamma Biradar',
    'Akash Bande',
    'Sharanu Alange',
];

const PartnerMeeting = () => {
    const [date, setDate] = useState('');
    const [overallPartners, setOverallPartners] = useState([]);
    const [overallLoading, setOverallLoading] = useState(false);
    const [overallError, setOverallError] = useState('');

    const API_BASE_URL =
        (process.env.REACT_APP_API_BASE_URL && process.env.REACT_APP_API_BASE_URL.trim()) ||
        (typeof window !== 'undefined' ? window.location.origin : 'http://localhost:5012');

    const toNumber = (value) => {
        const num = Number(value);
        return Number.isFinite(num) ? num : 0;
    };

    const normalizePartnerName = (value) =>
        String(value || '').trim().toUpperCase().replace(/\s+/g, '');

    const getOutstandingAmount = (row) => toNumber(row.outstanding_balance ?? row.remaining_amount);

    useEffect(() => {
        const today = new Date();
        setDate(today.toDateString());
    }, []);

    useEffect(() => {
        const fetchOverallPartners = async () => {
            setOverallLoading(true);
            setOverallError('');

            try {
                const partners = DEFAULT_PARTNERS;

                if (!partners.length) {
                    setOverallPartners([]);
                    return;
                }

                const [odResult, interestResult] = await Promise.all([
                    axios.post(
                        `${API_BASE_URL}/api/partner-meeting-od`,
                        { partners },
                        { timeout: 60000 }
                    ),
                    axios.post(
                        `${API_BASE_URL}/api/partner-meeting-interest`,
                        { partners },
                        { timeout: 60000 }
                    ),
                ]);
                const odByPartner = new Map(
                    (Array.isArray(odResult.data?.data) ? odResult.data.data : []).map((row) => [
                        normalizePartnerName(row.partnerName),
                        row,
                    ])
                );
                const interestByPartner = new Map(
                    (Array.isArray(interestResult.data?.data) ? interestResult.data.data : []).map((row) => [
                        normalizePartnerName(row.partnerName),
                        row,
                    ])
                );

                const summaries = await Promise.all(
                    partners.map(async (partner) => {
                        const partnerEnc = encodeURIComponent(partner);

                        const glDueP = axios.get(
                            `${API_BASE_URL}/api/general-loan-partnerwise-due-report?partner=${partnerEnc}`,
                            { timeout: 60000 }
                        );
                        const cdDueP = axios.get(
                            `${API_BASE_URL}/api/cd-loan-partnerwise-due-report?partner=${partnerEnc}`,
                            { timeout: 60000 }
                        );
                        const tbdDueP = axios.get(
                            `${API_BASE_URL}/api/tbd-loan-partnerwise-due-report?partner=${partnerEnc}`,
                            { timeout: 60000 }
                        );

                        const [glDueR, cdDueR, tbdDueR] = await Promise.allSettled([
                            glDueP,
                            cdDueP,
                            tbdDueP,
                        ]);

                        const glDue = glDueR.status === 'fulfilled' && Array.isArray(glDueR.value.data)
                            ? glDueR.value.data
                            : [];
                        const cdDue = cdDueR.status === 'fulfilled' && Array.isArray(cdDueR.value.data)
                            ? cdDueR.value.data
                            : [];
                        const tbdDue = tbdDueR.status === 'fulfilled' && Array.isArray(tbdDueR.value.data)
                            ? tbdDueR.value.data
                            : [];

                        const odRow = odByPartner.get(normalizePartnerName(partner)) || {};
                        const interestRow = interestByPartner.get(normalizePartnerName(partner)) || {};
                        // Interest = GL commission_amount (by recommended_by) + CD commission_interest_amount (by partner_name).
                        const interest = toNumber(interestRow.interest);
                        // OD = overdue_collected (GL via account_no) + cd_overdue_collected (CD via account_no), grouped by partner.
                        const od = toNumber(odRow.od);
                        const generalLoan = glDue.reduce((sum, row) => sum + getOutstandingAmount(row), 0);
                        const cdLoan = cdDue.reduce((sum, row) => sum + getOutstandingAmount(row), 0);
                        const tbdLoan = tbdDue.reduce((sum, row) => sum + getOutstandingAmount(row), 0);

                        return {
                            partnerName: partner,
                            interest,
                            od,
                            interestTotal: interest + od,
                            generalLoan,
                            cdLoan,
                            tbdLoan,
                            loanTotal: generalLoan + cdLoan + tbdLoan,
                        };
                    })
                );

                summaries.sort((a, b) => (a.partnerName || '').localeCompare(b.partnerName || ''));
                setOverallPartners(summaries);
            } catch (error) {
                console.error('Error fetching overall partners:', error);
                const status = error.response?.status;
                const apiError = error.response?.data?.error || error.response?.data?.message;
                const message = `Failed to load overall partner totals${status ? ` (HTTP ${status})` : ''}${apiError ? `: ${apiError}` : ''}`;
                setOverallError(message);
                setOverallPartners([]);
            } finally {
                setOverallLoading(false);
            }
        };

        fetchOverallPartners();
    }, [API_BASE_URL]);

    const grandTotal = useMemo(() => {
        return overallPartners.reduce(
            (totals, row) => ({
                interest: totals.interest + toNumber(row.interest),
                od: totals.od + toNumber(row.od),
                interestTotal: totals.interestTotal + toNumber(row.interestTotal),
                generalLoan: totals.generalLoan + toNumber(row.generalLoan),
                cdLoan: totals.cdLoan + toNumber(row.cdLoan),
                tbdLoan: totals.tbdLoan + toNumber(row.tbdLoan),
                loanTotal: totals.loanTotal + toNumber(row.loanTotal),
            }),
            {
                interest: 0,
                od: 0,
                interestTotal: 0,
                generalLoan: 0,
                cdLoan: 0,
                tbdLoan: 0,
                loanTotal: 0,
            }
        );
    }, [overallPartners]);

    const handlePrint = () => {
        const printContent = document.getElementById('partner-meeting-table-container')?.innerHTML;
        if (!printContent) {
            return;
        }

        const printWindow = window.open('', '_blank');
        printWindow.document.write(`
            <html>
                <head>
                    <title>Partners Meeting - ${FINANCE_NAME}</title>
                    <style>
                        body { font-family: Arial, sans-serif; padding: 20px; margin: 0; }
                        h1 { text-align: center; font-size: 22px; margin: 0 0 8px; text-transform: uppercase; }
                        h2 { text-align: center; font-size: 18px; margin: 0 0 8px; }
                        .print-date { text-align: center; font-size: 14px; margin: 0 0 20px; color: #333; }
                        table { width: 100%; border-collapse: collapse; font-size: 13px; }
                        th, td { border: 1px solid #ccc; padding: 8px; text-align: left; }
                        th { background-color: #0476df; color: #fff; font-weight: bold; }
                        td:not(:first-child), th:not(:first-child) { text-align: right; }
                        .partner-table-total-row td { font-weight: bold; background: #e8f4ff; }
                    </style>
                </head>
                <body>
                    <h1>${FINANCE_NAME}</h1>
                    <h2>Welcome to the Partners Meeting</h2>
                    <p class="print-date">Date: ${date || new Date().toDateString()}</p>
                    ${printContent}
                </body>
            </html>
        `);
        printWindow.document.close();
        printWindow.print();
    };

    return (
        <div className="partner-meeting-container">
            <h2 className="heading">Welcome to the Partners Meeting</h2>
            <p className="date">Date: {date}</p>

            <h3 className="overall-partners-heading">Overall Partners</h3>
            {overallLoading ? (
                <div className="overall-partners-loading">Loading overall partner totals...</div>
            ) : overallError ? (
                <div className="overall-partners-error">{overallError}</div>
            ) : (
                <>
                    <div id="partner-meeting-table-container">
                        <table className="partner-table overall-partners-table">
                            <thead>
                                <tr>
                                    <th>Partners name</th>
                                    <th>Interest</th>
                                    <th>OD</th>
                                    <th>Total (Interest + OD)</th>
                                    <th>General loan</th>
                                    <th>CD loan</th>
                                    <th>TBD</th>
                                    <th>Total (General loan + CD loan + TBD)</th>
                                </tr>
                            </thead>
                            <tbody>
                                {overallPartners.length === 0 ? (
                                    <tr>
                                        <td colSpan={8}>No partner data found.</td>
                                    </tr>
                                ) : (
                                    <>
                                        {overallPartners.map((p, idx) => (
                                            <tr key={`${p.partnerName || 'NA'}-${idx}`}>
                                                <td>{p.partnerName || 'N/A'}</td>
                                                <td>{Number(p.interest || 0).toFixed(2)}</td>
                                                <td>{Number(p.od || 0).toFixed(2)}</td>
                                                <td>{Number(p.interestTotal || 0).toFixed(2)}</td>
                                                <td>{Number(p.generalLoan || 0).toFixed(2)}</td>
                                                <td>{Number(p.cdLoan || 0).toFixed(2)}</td>
                                                <td>{Number(p.tbdLoan || 0).toFixed(2)}</td>
                                                <td>{Number(p.loanTotal || 0).toFixed(2)}</td>
                                            </tr>
                                        ))}
                                        <tr className="partner-table-total-row">
                                            <td>Total</td>
                                            <td>{grandTotal.interest.toFixed(2)}</td>
                                            <td>{grandTotal.od.toFixed(2)}</td>
                                            <td>{grandTotal.interestTotal.toFixed(2)}</td>
                                            <td>{grandTotal.generalLoan.toFixed(2)}</td>
                                            <td>{grandTotal.cdLoan.toFixed(2)}</td>
                                            <td>{grandTotal.tbdLoan.toFixed(2)}</td>
                                            <td>{grandTotal.loanTotal.toFixed(2)}</td>
                                        </tr>
                                    </>
                                )}
                            </tbody>
                        </table>
                    </div>
                    <button
                        type="button"
                        onClick={handlePrint}
                        className="print-button"
                        disabled={overallPartners.length === 0}
                    >
                        Print Table
                    </button>
                </>
            )}
        </div>  
    );
};

export default PartnerMeeting;
